// ───────────────────────────────────────────────────────────────────────────────
// ChainPlay connection + flow hooks (Base mainnet, theme-driven, hardened)
// - Keeps same public API: { SKIN, RADIUS, MAIN_MAX, useGameContracts, useGameFlow }
// - Adds: wrongNetwork flag + requestNetworkSwitch()
// - Fixes: invalid identifier in reveal log poller ("let stop<…>") → "let stop = false"
// ───────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Contract,
  formatUnits,
  parseUnits,
  solidityPackedKeccak256,
  hexlify,
  randomBytes,
  MaxUint256,
} from 'ethers';

import ChainPlayGameABI from '../../abis/ChainPlayGame.json';
import ChainPlayTokenABI from '../../abis/ChainPlayToken.json';
import {
  CONTRACT_ADDRESSES,
  CHAIN_ID as REQUIRED_CHAIN_ID,
  CHAIN_ID_HEX as REQUIRED_CHAIN_ID_HEX,
  CHAIN_NAME as REQUIRED_CHAIN_NAME,
} from '../../contracts/addresses'; // Base mainnet lock (8453)

import theme from '../../theme'; // central design tokens

// =======================================
// Visual / Theme tokens
// =======================================
export const MAIN_MAX = 760;
export const RADIUS = theme?.radii?.button || '6px';

const BASE = {
  bg: theme?.colors?.background || '#0A0E14',
  card: theme?.colors?.card || 'rgba(255,255,255,0.06)',
  glass: theme?.colors?.surface || 'rgba(255,255,255,0.06)',
  grid: '#ffffff14',
  neon1: '#7C4DFF',
  neon2: '#00E5FF',
  win: theme?.colors?.success || '#00E676',
  lose: theme?.colors?.danger || '#FF5252',
  gold: theme?.colors?.accentGold || '#FFD95E',
  ice: '#A1E3FF',
  text: theme?.colors?.text || '#e6f1ff',
  sub: theme?.colors?.subText || '#9db7c8',
};

export const SKIN = {
  ...BASE,
  header: theme?.colors?.background || '#0f1218',
  chrome: theme?.colors?.surface || '#141a22',
  accent: theme?.colors?.primary || '#36e0f8',
  shadow: theme?.shadows?.soft || '0 8px 20px rgba(0,0,0,0.28)',
};
export const ACCENT = SKIN.accent;

// =======================================
// SSR‑safe globals + small UI helpers
// =======================================
export function safeWindow() {
  try { return typeof window !== 'undefined' ? window : null; } catch { return null; }
}
export function safeDocument() {
  try { return typeof document !== 'undefined' ? document : null; } catch { return null; }
}
export function withAlpha(color, alpha = 0.5) {
  try {
    const a = Math.min(Math.max(Number(alpha), 0), 1);
    if (!color) return `rgba(255,255,255,${a})`;
    const c = String(color).trim();
    if (c.startsWith('rgba(')) {
      const parts = c.slice(5, -1).split(',').map(s=>s.trim());
      return `rgba(${parts[0]||0}, ${parts[1]||0}, ${parts[2]||0}, ${a})`;
    }
    if (c.startsWith('rgb(')) {
      const parts = c.slice(4, -1).split(',').map(s=>s.trim());
      return `rgba(${parts[0]||0}, ${parts[1]||0}, ${parts[2]||0}, ${a})`;
    }
    const hex = c.replace('#','');
    const full = hex.length === 3 ? hex.split('').map(h=>h+h).join('') : hex;
    const r = parseInt(full.slice(0,2), 16) || 0;
    const g = parseInt(full.slice(2,4), 16) || 0;
    const b = parseInt(full.slice(4,6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  } catch { return `rgba(255,255,255,${alpha || 0.5})`; }
}
export function ensureHttpUrl(url) {
  try {
    const u = new URL(String(url));
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.toString() : null;
  } catch { return null; }
}
export function openExternal(url) {
  const safe = ensureHttpUrl(url);
  if (!safe) return false;
  try { safeWindow()?.open?.(safe, '_blank', 'noopener,noreferrer'); return true; } catch { return false; }
}
export function formatUnitsShortSafe(v, decimals, opts) {
  try { return formatUnitsShort(v ?? '0', decimals ?? DEFAULT_DECIMALS, opts); }
  catch { return '—'; }
}

// =======================================
// Global constants
// =======================================
export const REVEAL_BLOCK_DELAY = 2;      // earliest reveal = commitBlock + this
export const DEFAULT_DECIMALS   = 18;
export const SETTLE_FREEZE_MS   = 850;
export const COOLDOWN_MS        = 220;
export const GLOBAL_FEED_LIMIT  = 30;
export const MAX_HISTORY        = 40;

const REVEAL_HASH_WINDOW       = 255;             // latest reveal = commitBlock + 255 (EVM blockhash horizon)
const PENDING_TTL_MS           = 24 * 60 * 60 * 1000; // safety TTL (24h)
const AUTO_REVEAL_DEBOUNCE_MS  = 650;             // guard against rapid‑fire reveals
const PERSIST_NS_VERSION       = 'v2';            // bump to invalidate older formats

// =======================================
// Game type (named enums)
// =======================================
export const GAME_TYPE = Object.freeze({
  LIMBO: 0,
  COINFLIP: 1,
  DICE: 2,
  HILO: 4,
  MINES: 6,
  CRASH: 7,
  PLINKO: 8,
  KENO: 9,
});

// =======================================
// Global flags (stored once for the whole UI)
// =======================================
export const clamp = (n, min, max) => Math.min(Math.max(Number(n), min), max);

function safeLS() { try { return safeWindow()?.localStorage || null; } catch { return null; } }
function getJSON(key, fallback) {
  try { const s = safeLS()?.getItem(key); return s ? JSON.parse(s) : fallback; } catch { return fallback; }
}
function setJSON(key, val)  { try { safeLS()?.setItem(key, JSON.stringify(val)); } catch {} }
function del(key)           { try { safeLS()?.removeItem(key); } catch {} }

function getGlobalFlags() {
  try {
    const qs = new URLSearchParams(safeWindow()?.location?.search || '');
    const fromQs = (qs.get('flags') || '').split(',').map(s => s.trim()).filter(Boolean);
    const key = 'ui.flags';
    const fromStore = getJSON(key, []);
    const merged = Array.from(new Set([...fromStore, ...fromQs]));
    setJSON(key, merged);
    return new Set(merged);
  } catch { return new Set(); }
}
export const FLAGS = getGlobalFlags();

// =======================================
// Shared helpers
// =======================================
export function uiToRawMultiplier(mult, param1MaxRaw = 255) {
  const raw = Math.floor(Number(mult) * 100);
  return clamp(raw, 0, Math.min(255, Number(param1MaxRaw)));
}
export function rawToUiMultiplier(raw) {
  return Math.round(Number(raw) || 0) / 100;
}
export function newSecret() { return hexlify(randomBytes(32)); }
export const shortAddr = (a, l = 4) => (a ? `${a.slice(0, 2 + l)}…${a.slice(-l)}` : '—');
export async function copy(text) {
  try { await navigator.clipboard.writeText(text); toast.success('Copied'); }
  catch { toast.info(text); }
}
export function toBigIntDecimal(s) { try { return parseUnits(String(s ?? '0'), 0); } catch { return 0n; } }
export function formatUnitsShort(v, decimals, { maxFrac = 4 } = {}) {
  try {
    if (typeof v === 'bigint' && v >= MaxUint256 / 2n) return 'Unlimited';
    const s = formatUnits(v || 0n, decimals);
    const [int, frac = ''] = s.split('.');
    if (int.length >= 6) {
      const n = Number(s);
      if (Number.isFinite(n))
        return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 2 }).format(n);
      return int;
    }
    const f = frac.slice(0, maxFrac).replace(/0+$/, '');
    return f ? `${int}.${f}` : int;
  } catch { return '0'; }
}
export function formatAllowance(v, decimals) { return formatUnitsShort(v, decimals); }

// ERC‑721 probe for boost NFT
const ERC721_ABI = [
  {
    inputs: [{ internalType: 'address', name: 'owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
];

export function dayId(d = new Date()) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
export function isoWeekId(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

// =======================================
/** Explorer helpers */
// =======================================
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
export function explorerBase(chainId) {
  switch (Number(chainId)) {
    case 1: return 'https://etherscan.io';
    case 5: return 'https://goerli.etherscan.io';
    case 10: return 'https://optimistic.etherscan.io';
    case 56: return 'https://bscscan.com';
    case 137: return 'https://polygonscan.com';
    case 42161: return 'https://arbiscan.io';
    case 8453: return 'https://basescan.org';
    case 43114: return 'https://snowtrace.io';
    case 250: return 'https://ftmscan.com';
    // Testnets
    case 11155111: return 'https://sepolia.etherscan.io';
    case 421614: return 'https://sepolia.arbiscan.io';
    default: return null;
  }
}
export function txLink(chainId, txHash) {
  const base = explorerBase(chainId);
  return base ? `${base}/tx/${txHash}` : null;
}

// =======================================
/** WebAudio SFX hook */
// =======================================
export function useSfx(muted, volume = 0.8, haptics = true) {
  const ctxRef = useRef(null);
  const ensureCtx = () => {
    if (muted) return null;
    if (!ctxRef.current) {
      try {
        const w = safeWindow();
        const AC = w && (w.AudioContext || w.webkitAudioContext);
        if (AC) ctxRef.current = new AC();
      } catch {}
    }
    return ctxRef.current;
  };

  useEffect(() => {
    const tryResume = () => {
      try { const ctx = ctxRef.current; if (ctx && ctx.state === 'suspended') ctx.resume?.(); } catch {}
    };
    const w = safeWindow(), d = safeDocument();
    try { w?.addEventListener?.('pointerdown', tryResume, { passive: true }); } catch {}
    try { d?.addEventListener?.('visibilitychange', tryResume); } catch {}
    return () => {
      try { w?.removeEventListener?.('pointerdown', tryResume); } catch {}
      try { d?.removeEventListener?.('visibilitychange', tryResume); } catch {}
    };
  }, []);

  const blip = (freq = 440, dur = 0.08, type = 'triangle', vol = 0.2) => {
    const ctx = ensureCtx(); if (!ctx) return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    const v = clamp(vol * volume, 0, 0.7);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(v, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(t0 + dur + 0.02);
  };
  const vib = (pat) => { try { haptics && navigator.vibrate?.(pat); } catch {} };
  return {
    commit: () => { blip(520, 0.09, 'sawtooth', 0.18); vib(20); },
    ready:  () => blip(760, 0.08, 'square', 0.15),
    win:    () => { blip(660, 0.08); setTimeout(() => blip(880, 0.08), 70); setTimeout(() => blip(1170, 0.1), 140); vib([15,20,15]); },
    lose:   () => { blip(220, 0.18, 'sine', 0.22); vib(40); },
    tick:   () => vib(10),
  };
}

// =======================================
// Storage namespace helpers (per game, per chain/account)
// =======================================
function nsFor(gameType) {
  return `game.${PERSIST_NS_VERSION}`; // unified namespace for all non‑legacy pages
}
function makeKeys(chainId, gameType) {
  const NS = nsFor(gameType);
  const gt = Number(gameType);
  const ch = String(chainId || '0');
  return {
    NS,
    flags: `${NS}.flags`,
    pending: (addr) => `${NS}.pending:${ch}:${gt}:${addr?.toLowerCase?.()}`,
    history: (addr) => `${NS}.history:${ch}:${gt}:${addr?.toLowerCase?.()}`,
    prefs: (addr) => `${NS}.prefs:${ch}:${gt}:${addr?.toLowerCase?.() || 'anon'}`,
    progress: (addr) => `${NS}.progress:${ch}:${gt}:${addr?.toLowerCase?.() || 'anon'}`,
  };
}

// =======================================
// Progress defaults (hoisted to fix ESLint deps)
// =======================================
const DEFAULT_PROG = Object.freeze({
  daily: { dayId: null, count: 0 },
  weekly: { weekId: null, near150Wins: 0 },
  season: { xp: 0 },
});

// =======================================
/** Contracts hook (GENERIC) */
// =======================================
export function useGameContracts(signer, gameType) {
  const [game, setGame] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenDecimals, setTokenDecimals] = useState(DEFAULT_DECIMALS);
  const [tokenSymbol, setTokenSymbol] = useState('TOKEN');
  const [account, setAccount] = useState(null);
  const [paused, setPaused] = useState(false);
  const [maxProfit, setMaxProfit] = useState(0n);
  const [config, setConfig] = useState({
    active: false,
    minBet: 0n,
    maxBet: 0n,
    houseEdge: 0n,
    param1Max: 255n,
    param2Max: 0n,
  });
  const [balance, setBalance] = useState(0n);
  const [allowance, setAllowance] = useState(0n);
  const [boostAddr, setBoostAddr] = useState(null);
  const [boostActive, setBoostActive] = useState(false);
  const [chainId, setChainId] = useState(null);

  const provider = signer?.provider;

  // Instantiate contracts + account
  useEffect(() => {
    if (!signer) { setGame(null); setToken(null); setAccount(null); return; }
    const gameContract  = new Contract(CONTRACT_ADDRESSES.ChainPlayGame,  ChainPlayGameABI,  signer);
    const tokenContract = new Contract(CONTRACT_ADDRESSES.ChainPlayToken, ChainPlayTokenABI, signer);
    setGame(gameContract);
    setToken(tokenContract);
    (async () => { try { const addr = await signer.getAddress(); setAccount(addr); } catch {} })();
  }, [signer]);

  // Token meta
  useEffect(() => {
    if (!token) return;
    (async () => {
      try { setTokenDecimals(Number(await token.decimals())); } catch { setTokenDecimals(DEFAULT_DECIMALS); }
      try { const sym = await token.symbol(); if (sym) setTokenSymbol(String(sym)); } catch {}
    })();
  }, [token]);

  const refreshBasics = useCallback(async () => {
    if (!game || !token || !account || gameType == null) return;
    try {
      const [paused_, maxProfit_, cfg, bal, allow, boostNftAddr] = await Promise.all([
        game.paused(),
        game.maxProfit(),
        game.gameConfigs(Number(gameType)),
        token.balanceOf(account),
        token.allowance(account, CONTRACT_ADDRESSES.ChainPlayGame),
        game.boostNFT ? game.boostNFT() : ZERO_ADDRESS,
      ]);
      setPaused(Boolean(paused_));
      setMaxProfit(maxProfit_);
      const [active, minBet, maxBet, houseEdge, param1Max, param2Max] = cfg;
      setConfig({ active, minBet, maxBet, houseEdge, param1Max, param2Max });
      setBalance(bal);
      setAllowance(allow);
      setBoostAddr(boostNftAddr);
    } catch (err) {
      console.error('refreshBasics error:', err);
    }
  }, [game, token, account, gameType]);

  // Track network changes
  useEffect(() => {
    if (!provider) return;
    let alive = true;
    (async () => {
      try { const net = await provider.getNetwork(); if (alive) setChainId(Number(net?.chainId || 0)); } catch {}
    })();
    const onNet = (...args) => {
      const evt = args[0];
      const next = evt?.chainId ?? args[1]?.chainId ?? evt?.network?.chainId ?? 0;
      setChainId(Number(next || 0));
    };
    try { provider.on?.('network', onNet); } catch {}
    const t = setInterval(async () => {
      try {
        const net = await provider.getNetwork();
        const cid = Number(net?.chainId || 0);
        setChainId((old) => (old !== cid ? cid : old));
      } catch {}
    }, 7000);
    return () => {
      alive = false;
      clearInterval(t);
      try { provider.off?.('network', onNet); } catch {}
    };
  }, [provider]);

  // Boost NFT check
  useEffect(() => {
    let stop = false;
    (async () => {
      if (!boostAddr || !account || !provider) { setBoostActive(false); return; }
      try {
        if (String(boostAddr).toLowerCase() === ZERO_ADDRESS) { setBoostActive(false); return; }
        const erc721 = new Contract(boostAddr, ERC721_ABI, provider);
        const bal = await erc721.balanceOf(account);
        if (!stop) setBoostActive((bal ?? 0n) > 0n);
      } catch { if (!stop) setBoostActive(false); }
    })();
    return () => { stop = true; };
  }, [boostAddr, account, provider]);

  // Refresh on config-ish events
  useEffect(() => {
    if (!game || gameType == null) return;
    refreshBasics();
    const onCfg = (gt) => { if (Number(gt) === Number(gameType)) refreshBasics(); };
    const onMaxProfit = () => refreshBasics();
    const onPaused = () => refreshBasics();
    const onUnpaused = () => refreshBasics();
    try {
      game.on('GameConfigUpdated', onCfg);
      game.on('MaxProfitUpdated', onMaxProfit);
      game.on('Paused', onPaused);
      game.on('Unpaused', onUnpaused);
    } catch {}
    const t1 = setInterval(refreshBasics, 20000);
    return () => {
      try { game.off('GameConfigUpdated', onCfg); game.off('MaxProfitUpdated', onMaxProfit); game.off('Paused', onPaused); game.off('Unpaused', onUnpaused); } catch {}
      clearInterval(t1);
    };
  }, [game, gameType, refreshBasics]);

  return {
    gameType: Number(gameType),
    game,
    token,
    tokenDecimals,
    tokenSymbol,
    account,
    paused,
    maxProfit,
    config,
    balance,
    allowance,
    provider,
    boostActive,
    chainId,
  };
}

// =======================================
/** Full game flow hook (GENERIC) */
// =======================================
export function useGameFlow(contracts, options, gameTypeArg) {
  const {
    gameType: contractsType,
    game,
    token,
    tokenDecimals,
    tokenSymbol,
    account,
    paused,
    maxProfit,
    config,
    allowance,
    provider,
    chainId,
  } = contracts || {};
  const gameType = Number(gameTypeArg != null ? gameTypeArg : contractsType);

  // Wrong-network detection + switch helper (Base mainnet only)
  const wrongNetwork = Number(chainId || 0) !== Number(REQUIRED_CHAIN_ID);
  const requestNetworkSwitch = useCallback(async () => {
    const eth = safeWindow()?.ethereum;
    if (!eth) { toast.info(`Please switch to ${REQUIRED_CHAIN_NAME} in your wallet.`); return false; }
    try {
      await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: REQUIRED_CHAIN_ID_HEX }] });
      return true;
    } catch (err) {
      // Chain not added
      if (err?.code === 4902 || String(err?.message || '').includes('4902')) {
        try {
          await eth.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: REQUIRED_CHAIN_ID_HEX,
              chainName: 'Base',
              nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
              rpcUrls: ['https://mainnet.base.org'],
              blockExplorerUrls: ['https://basescan.org'],
            }],
          });
          return true;
        } catch {}
      }
      toast.error(`Switch to ${REQUIRED_CHAIN_NAME} to play.`);
      return false;
    }
  }, []);

  // Keys are scoped per chain+gameType
  const keys = useMemo(() => makeKeys(chainId, gameType), [chainId, gameType]);

  const revealDelay = Math.max(0, Number(options?.revealBlockDelay ?? REVEAL_BLOCK_DELAY));

  // Param packers (default → overridable at runtime)
  const packersRef = useRef({
    toParam1Raw: options?.toParam1Raw ?? ((mult, p1max) => uiToRawMultiplier(mult, p1max)),
    toParam2Raw: options?.toParam2Raw ?? (() => 0),
  });
  const setParamPackers = useCallback((p) => {
    if (!p) return;
    packersRef.current = {
      toParam1Raw: p.toParam1Raw || packersRef.current.toParam1Raw,
      toParam2Raw: p.toParam2Raw || packersRef.current.toParam2Raw,
    };
  }, []);

  const milestoneTarget = Number(options?.milestoneTarget ?? 1.5);
  const { volume, haptics } = options || {};
  const tag = useMemo(() => `[game:${gameType}]`, [gameType]);

  // logger wiring
  const logRef = useRef(options?.appendLog || (() => {}));
  useEffect(() => { logRef.current = options?.appendLog || (() => {}); }, [options?.appendLog]);
  const log = useCallback((level, msg, meta) => {
    try { logRef.current?.(level, msg, meta); } catch {}
    try {
      if (level === 'error') console.error(tag, msg, meta || '');
      else if (level === 'warn') console.warn(tag, msg, meta || '');
      else console.log(tag, msg, meta || '');
    } catch {}
  }, [tag]);

  const [status, setStatus] = useState('IDLE');
  const [betInput, setBetInput] = useState('1');
  const [multiplier, setMultiplier] = useState(1.5);
  const [autoReveal, setAutoReveal] = useState(true);

  const [tab, setTab] = useState(0);
  const [autoRounds, setAutoRounds] = useState(0);
  const autoEnabled = tab === 1 && autoRounds > 0;

  const [onChainPending, setOnChainPending] = useState(false);

  const [pending, setPending] = useState(null);
  const [blockNumber, setBlockNumber] = useState(0);
  const [lastBlockTs, setLastBlockTs] = useState(Date.now());
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [confetti, setConfetti] = useState(false);
  const [muted, setMuted] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [highContrast, setHighContrast] = useState(false);
  const [globalFeed, setGlobalFeed] = useState([]);

  const [prog, setProg] = useState(DEFAULT_PROG);

  const sessionStartRef = useRef(Date.now());
  const [sessionSecs, setSessionSecs] = useState(0);

  const [guardEnabled, setGuardEnabled] = useState(false);
  const [guardStopLoss, setGuardStopLoss] = useState('0');
  const [guardTakeProfit, setGuardTakeProfit] = useState('0');
  const [sessionPnL, setSessionPnL] = useState(0n);

  const readyAtRef = useRef(0);
  const revealTriggeredAtRef = useRef(0);
  const lastRevealAttemptRef = useRef({ t: 0, block: -1 });

  const [revealWatch, setRevealWatch] = useState(null); // { txHash, blockHint, startedAt, manual }
  const [pendingTx, setPendingTx] = useState(null);     // { hash, type, startedAt }

  const sfx = useSfx(muted, volume, haptics);

  // Local per‑game storage helpers (stable by keys)
  const savePending = useCallback((address, p) => setJSON(keys.pending(address), p), [keys]);
  const loadPending = useCallback((address) => getJSON(keys.pending(address), null), [keys]);
  const clearPending = useCallback((address) => del(keys.pending(address)), [keys]);
  const loadHistory = useCallback((address) => getJSON(keys.history(address), []), [keys]);
  const saveHistory = useCallback((address, arr) => setJSON(keys.history(address), arr), [keys]);
  const loadPrefs = useCallback((address) => getJSON(keys.prefs(address), {}), [keys]);
  const savePrefs = useCallback((address, obj) => setJSON(keys.prefs(address), obj), [keys]);
  const loadProg = useCallback((address) => {
    const s = getJSON(keys.progress(address), DEFAULT_PROG);
    return { daily: s.daily || DEFAULT_PROG.daily, weekly: s.weekly || DEFAULT_PROG.weekly, season: s.season || DEFAULT_PROG.season };
  }, [keys]);
  const saveProg = useCallback((address, obj) => setJSON(keys.progress(address), obj), [keys]);

  // Prefs/progress
  useEffect(() => {
    const p = loadPrefs(account);
    if (p?.reducedMotion != null) setReducedMotion(Boolean(p.reducedMotion));
    if (p?.highContrast != null) setHighContrast(Boolean(p.highContrast));
    const pr = loadProg(account);
    setProg(pr);
  }, [account, loadPrefs, loadProg]);
  useEffect(() => { savePrefs(account, { reducedMotion, highContrast }); }, [account, reducedMotion, highContrast, savePrefs]);
  useEffect(() => { saveProg(account, prog); }, [account, prog, saveProg]);

  // Session timer + gentle nudges
  useEffect(() => {
    sessionStartRef.current = Date.now();
    setSessionSecs(0);
    const id = setInterval(() => setSessionSecs(Math.floor((Date.now() - sessionStartRef.current) / 1000)), 1000);
    const n30 = setTimeout(() => toast.info('Quick breather? 30 minutes in.'), 30 * 60 * 1000);
    const n60 = setTimeout(() => toast.info('60 minutes — stretch and hydrate.'), 60 * 60 * 1000);
    return () => { clearInterval(id); clearTimeout(n30); clearTimeout(n60); };
  }, [account]);

  // History persistence
  const pushHistory = useCallback((rec) => {
    setHistory((arr) => {
      const next = [rec, ...arr].slice(0, MAX_HISTORY);
      try { if (account) saveHistory(account, next); } catch {}
      return next;
    });
  }, [account, saveHistory]);
  useEffect(() => { if (account) setHistory(loadHistory(account)); }, [account, loadHistory]);

  // Block subscription (with fallback polling)
  useEffect(() => {
    if (!provider) return;
    let lastEvent = Date.now();
    const onBlock = (bn) => { setBlockNumber(Number(bn)); lastEvent = Date.now(); setLastBlockTs(Date.now()); };
    try { provider.on('block', onBlock); } catch {}
    const poll = setInterval(async () => {
      if (Date.now() - lastEvent > 10000) {
        try { const bn = await provider.getBlockNumber(); setBlockNumber(Number(bn)); setLastBlockTs(Date.now()); } catch {}
      }
    }, 5000);
    return () => { try { provider.off('block', onBlock); } catch {} clearInterval(poll); };
  }, [provider]);

  // Self‑check for on‑chain pending
  useEffect(() => {
    if (!game || !account) return;
    let stop = false;
    const loop = async () => {
      if (stop) return;
      try { setOnChainPending(Boolean(await game.hasPendingReveal(account))); } catch {}
      setTimeout(loop, document.visibilityState === 'visible' ? 1500 : 4000);
    };
    loop();
    return () => { stop = true; };
  }, [game, account]);

  // Recover pending from storage (TTL + window check)
  useEffect(() => {
    if (!account) return;
    const saved = loadPending(account);
    if (saved?.commitment && saved?.secret && saved?.param1Raw !== undefined) {
      const tooOld = saved.ts && (Date.now() - Number(saved.ts) > PENDING_TTL_MS);
      if (!tooOld) {
        setPending(saved);
        setStatus('WAITING');
        log('info', 'Recovered pending commit from storage', { block: saved.commitBlock, commitment: saved.commitment });
      } else {
        clearPending(account);
      }
    }
  }, [account, log, loadPending, clearPending]);

  // Watch GameCommitted to backfill commitBlock
  useEffect(() => {
    if (!game || !account) return;
    const filter = game.filters?.GameCommitted ? game.filters.GameCommitted(null, account) : 'GameCommitted';
    const onCommitted = (...args) => {
      try {
        let commitment, player, ev, gt;
        if (args[0]?.args) {
          const a = args[0].args; ({ commitment } = a); player = a.player; gt = a.gameType ?? a[3]; ev = args[0];
        } else { [commitment, player, , gt, ev] = args; }
        if (String(player).toLowerCase() !== account.toLowerCase()) return;
        if (Number(gt) !== Number(gameType)) return;
        if (pending && String(pending.commitment).toLowerCase() === String(commitment).toLowerCase()) {
          const evBlock = ev?.blockNumber ?? ev?.log?.blockNumber ?? null;
          if (evBlock != null && (!pending.commitBlock || pending.commitBlock === 0)) {
            const next = { ...pending, commitBlock: Number(evBlock), ts: pending.ts || Date.now() };
            setPending(next); savePending(account, next); setStatus('WAITING');
            log('success', 'GameCommitted event', { block: evBlock, commitment: String(commitment) });
          }
        }
      } catch {}
    };
    try { game.on(filter, onCommitted); } catch {}
    return () => { try { game.off(filter, onCommitted); } catch {} };
  }, [game, account, pending, log, gameType, savePending]);

  // Reveal window (single declaration)
  const earliestBlock = useMemo(() => pending?.commitBlock == null ? null : Number(pending.commitBlock) + Number(revealDelay), [pending?.commitBlock, revealDelay]);
  const latestBlock   = useMemo(() => pending?.commitBlock == null ? null : Number(pending.commitBlock) + REVEAL_HASH_WINDOW, [pending?.commitBlock]);
  const isExpired     = useMemo(() => (latestBlock == null || !blockNumber) ? false : Number(blockNumber) > Number(latestBlock), [latestBlock, blockNumber]);

  // Ready state when revealDelay satisfied
  const [wasReady, setWasReady] = useState(false);
  const revealReady = useMemo(() => (!pending?.commitBlock || !blockNumber) ? false : Number(blockNumber) >= Number(pending.commitBlock) + Number(revealDelay), [pending, blockNumber, revealDelay]);

  // Extra guard: only reveal within the valid window
  const inRevealWindow = revealReady && !isExpired;

  useEffect(() => {
    if (revealReady && !wasReady) {
      readyAtRef.current = Date.now();
      setWasReady(true);
      sfx.ready();
      setStatus((st) => (st === 'WAITING' ? 'READY' : st));
      log('info', 'Reveal is ready', { commitBlock: pending?.commitBlock, currentBlock: blockNumber });
    }
    if (!revealReady) setWasReady(false);
  }, [revealReady, wasReady, sfx, log, blockNumber, pending?.commitBlock]);

  // Subtle haptic tick while waiting and not auto‑revealing
  useEffect(() => {
    if (revealReady && (status === 'READY' || status === 'WAITING') && !autoReveal) {
      const id = setInterval(() => sfx.tick(), 700);
      return () => clearInterval(id);
    }
  }, [revealReady, status, autoReveal, sfx]);

  // Multiplier slider bounds based on config
  const param1MaxRaw = config?.param1Max ?? 255n;
  const sliderMax    = rawToUiMultiplier(param1MaxRaw);
  const sliderBounds = useMemo(() => {
    const max = Number.isFinite(sliderMax) && sliderMax > 1 ? sliderMax : 2.55;
    return { min: 1.0, max: Math.max(1.01, max) };
  }, [sliderMax]);
  const setMultiplierSafe = useCallback((v) => {
    const n = Array.isArray(v) ? Number(v[0]) : Number(v);
    const val = Number.isFinite(n) ? n : 1.5;
    setMultiplier(clamp(val, sliderBounds.min, sliderBounds.max));
  }, [sliderBounds]);
  const setRollOver = useCallback((v) => setMultiplierSafe(v), [setMultiplierSafe]);
  useEffect(() => { setMultiplier((m0) => clamp(Number.isFinite(m0) ? m0 : 1.5, sliderBounds.min, sliderBounds.max)); }, [sliderBounds]);

  // Bet math & caps
  const minBetUnits = config?.minBet ?? 0n;
  const maxBetUnits = config?.maxBet ?? 0n;
  const betUnits = useMemo(() => { try { return parseUnits(betInput || '0', tokenDecimals || DEFAULT_DECIMALS); } catch { return 0n; } }, [betInput, tokenDecimals]);
  const betTooLow  = betUnits < minBetUnits;
  const betTooHigh = maxBetUnits > 0n && betUnits > maxBetUnits;

  const estPayout = useMemo(() => {
    try {
      const mult100 = Math.round(multiplier * 100);
      const mult100n = parseUnits(String(mult100), 0);
      return (betUnits * mult100n) / 100n;
    } catch { return 0n; }
  }, [betUnits, multiplier]);
  const estProfit = estPayout > betUnits ? estPayout - betUnits : 0n;
  const profitExceedsCap = maxProfit > 0n && estProfit > maxProfit;

  // One‑at‑a‑time guard for on‑chain actions
  const [busyAction, setBusyAction] = useState(false);
  const runLocked = useCallback(async (_label, fn) => {
    if (busyAction) return;
    setBusyAction(true);
    try { return await fn(); }
    finally { setBusyAction(false); }
  }, [busyAction]);

  // Allowance helpers
  const ensureAllowance = useCallback(
    async (required) => {
      if (!token || !account) return;
      const current = await token.allowance(account, CONTRACT_ADDRESSES.ChainPlayGame);
      log('info', 'Checking allowance', { needed: required?.toString?.() || String(required), current: current?.toString?.() || String(current) });
      if (current >= required) return;
      log('warn', 'Allowance insufficient; requesting approval', { to: CONTRACT_ADDRESSES.ChainPlayGame, amount: required?.toString?.() || String(required) });
      const tx = await token.approve(CONTRACT_ADDRESSES.ChainPlayGame, required);
      setPendingTx({ hash: tx.hash, type: 'approve', startedAt: Date.now() });
      log('info', 'Approve tx sent', { hash: tx.hash });
      await tx.wait();
      setPendingTx(null);
      toast.success('Allowance updated');
      log('success', 'Allowance updated', { hash: tx.hash });
    },
    [token, account, log]
  );

  const approveExact = useCallback(() =>
    runLocked('ApproveExact', async () => {
      if (!token) return;
      const tx = await token.approve(CONTRACT_ADDRESSES.ChainPlayGame, betUnits);
      setPendingTx({ hash: tx.hash, type: 'approve', startedAt: Date.now() });
      log('info', 'Approve exact sent', { hash: tx.hash, amount: betUnits?.toString?.() });
      await tx.wait();
      setPendingTx(null);
      toast.success('Approved exact amount');
      log('success', 'Approve exact confirmed', { hash: tx.hash });
    }), [runLocked, token, betUnits, log]
  );

  const approveUnlimited = useCallback(() =>
    runLocked('ApproveUnlimited', async () => {
      if (!token) return;
      const tx = await token.approve(CONTRACT_ADDRESSES.ChainPlayGame, MaxUint256);
      setPendingTx({ hash: tx.hash, type: 'approve', startedAt: Date.now() });
      log('info', 'Approve unlimited sent', { hash: tx.hash });
      await tx.wait();
      setPendingTx(null);
      toast.success('Approved unlimited');
      log('success', 'Approve unlimited confirmed', { hash: tx.hash });
    }), [runLocked, token, log]
  );

  // Auto‑purge stale pending from storage when crossing the window
  useEffect(() => {
    if (!account || !pending) return;
    if (isExpired) {
      clearPending(account);
      setPending(null);
      setStatus('IDLE');
      setOnChainPending(false);
      log('warn', 'Purged expired pending round from storage');
    }
  }, [account, pending, isExpired, clearPending, log]);

  // Commit
  const commit = useCallback(() =>
    runLocked('Commit', async () => {
      if (!game || !account) return;
      if (wrongNetwork) { toast.error(`Wrong network. Switch to ${REQUIRED_CHAIN_NAME}.`); await requestNetworkSwitch(); return; }
      if (paused)        { toast.error('Game is paused'); log('warn', 'Commit blocked: paused'); return; }
      if (!config?.active) { toast.error('Game is inactive'); log('warn', 'Commit blocked: inactive'); return; }
      if (onChainPending)  { toast.error('Finish reveal first'); log('warn', 'Commit blocked: pending reveal (on‑chain)'); return; }
      if (pending && !isExpired) { toast.error('Pending round exists — reveal or expire it'); log('warn', 'Commit blocked: local pending'); return; }
      if (betTooLow)  { toast.error('Bet below minimum'); log('warn', 'Commit blocked: below min', { min: String(minBetUnits) }); return; }
      if (betTooHigh) { toast.error('Bet above maximum'); log('warn', 'Commit blocked: above max', { max: String(maxBetUnits) }); return; }
      if (profitExceedsCap) { toast.error('Projected profit exceeds maxProfit'); log('warn', 'Commit blocked: exceeds profit cap'); return; }

      try {
        if (await game.hasPendingReveal(account)) {
          setOnChainPending(true);
          toast.error('Pending reveal detected');
          log('warn', 'Commit blocked: on‑chain pending');
          return;
        }
      } catch {}

      setLastResult(null);

      const secret = newSecret();
      const p = packersRef.current;
      const param1Raw = p.toParam1Raw(multiplier, Number(config?.param1Max ?? 255n));
      const param2Raw = p.toParam2Raw(multiplier, Number(config?.param2Max ?? 0n));
      const commitment = solidityPackedKeccak256(['bytes32', 'uint8', 'uint8'], [secret, param1Raw, param2Raw]);

      log('info', 'Commit requested', {
        amount: formatUnits(betUnits || 0n, tokenDecimals || DEFAULT_DECIMALS),
        targetX: multiplier,
        commitment,
        gameType,
      });

      await ensureAllowance(betUnits);

      try {
        const tx = await game.commitPlay(Number(gameType), betUnits, commitment);
        setPendingTx({ hash: tx.hash, type: 'commit', startedAt: Date.now() });
        log('info', 'Commit tx sent', { hash: tx.hash });
        const receipt = await tx.wait();
        setPendingTx(null);

        let commitBlock = receipt?.blockNumber ?? null;
        if (commitBlock == null) {
          try { const cm = await game.commits(commitment); if (cm?.commitBlock) commitBlock = Number(cm.commitBlock); } catch {}
        }
        if (commitBlock == null) {
          try { commitBlock = Number(await provider.getBlockNumber()); } catch { commitBlock = 0; }
        }

        const next = {
          version: PERSIST_NS_VERSION,
          ts: Date.now(),
          secret,
          commitment,
          param1Raw,
          param2Raw,
          betUnits: betUnits.toString(),
          gameType: Number(gameType),
          commitBlock: Number(commitBlock || 0),
        };
        savePending(account, next);
        setPending(next);
        setOnChainPending(true);
        setStatus('WAITING');
        sfx.commit();
        toast.success('Bet committed');
        log('success', 'Commit confirmed', { block: commitBlock, hash: tx.hash, commitment });
      } catch (err) {
        setPendingTx(null);
        setStatus('IDLE');
        toast.error(err?.shortMessage || err?.message || 'Commit failed');
        log('error', 'Commit failed', { message: err?.message || String(err) });
      }
    }),
  [
    runLocked, game, account, wrongNetwork, requestNetworkSwitch,
    paused, config?.active, onChainPending, pending, isExpired,
    betTooLow, betTooHigh, profitExceedsCap, multiplier,
    ensureAllowance, betUnits, provider, tokenDecimals, sfx, log,
    minBetUnits, maxBetUnits, gameType, savePending, config?.param1Max, config?.param2Max, setLastResult,
  ]);

  // Progress/xp helpers
  const addXp = useCallback((delta) => {
    setProg((p) => ({ ...p, season: { xp: Math.max(0, Math.floor((p.season?.xp || 0) + delta)) } }));
  }, []);
  const updateDaily = useCallback(() => {
    setProg((p) => {
      const today = dayId();
      const cur = p.daily || { dayId: null, count: 0 };
      if (cur.dayId === today) return { ...p, daily: { dayId: today, count: Math.min(5, (cur.count || 0) + 1) } };
      return { ...p, daily: { dayId: today, count: 1 } };
    });
  }, []);
  const updateWeeklyMilestone = useCallback((rec) => {
    const m = Number((rec.actualMult ?? rec.multTarget) || 1);
    const near = Math.abs(m - milestoneTarget) <= 0.01 && !rec.isLoss;
    if (!near) return;
    setProg((p) => {
      const wk = isoWeekId();
      const cur = p.weekly || { weekId: null, near150Wins: 0 };
      if (cur.weekId === wk) return { ...p, weekly: { weekId: wk, near150Wins: Math.min(3, (cur.near150Wins || 0) + 1) } };
      return { ...p, weekly: { weekId: wk, near150Wins: 1 } };
    });
  }, [milestoneTarget]);

  // Apply result & settle
  const applyResult = useCallback((payout, isLoss, meta = {}) => {
    const betNow = pending?.betUnits ? toBigIntDecimal(pending.betUnits) : 0n;
    const delta  = isLoss ? 0n - betNow : (payout ?? 0n) - betNow;

    let actualMult = null;
    try {
      if (!isLoss && betNow > 0n) {
        const pay = Number(formatUnits(payout || 0n, tokenDecimals || DEFAULT_DECIMALS));
        const b   = Number(formatUnits(betNow, tokenDecimals || DEFAULT_DECIMALS));
        if (Number.isFinite(pay) && Number.isFinite(b) && b > 0) actualMult = pay / b;
      }
    } catch {}

    let timingBadge = null;
    if (meta.manual) {
      const dt = Math.max(0, (revealTriggeredAtRef.current || 0) - (readyAtRef.current || 0));
      if      (dt <= 400)  timingBadge = 'Perfect';
      else if (dt <= 900)  timingBadge = 'Great';
      else if (dt <= 1500) timingBadge = 'Good';
    }

    const rec = {
      isLoss: Boolean(isLoss),
      payout,
      at: Date.now(),
      bet: pending?.betUnits || '0',
      multTarget: pending ? rawToUiMultiplier(pending.param1Raw) : Number(multiplier),
      actualMult,
      txHash: meta.txHash,
      secret: pending?.secret,
      commitment: pending?.commitment,
      timingBadge,
    };
    setLastResult(rec);
    pushHistory({ ...rec, mult: rec.actualMult ?? rec.multTarget });
    setSessionPnL((p) => p + delta);

    if (!rec.isLoss) {
      setConfetti(true); setTimeout(() => setConfetti(false), 1800);
      sfx.win();  addXp(12);
    } else {
      sfx.lose(); addXp(6);
    }
    updateDaily(); updateWeeklyMilestone(rec);

    setOnChainPending(false);
    clearPending(account);
    setPending(null);
    setStatus('SETTLED');
    setRevealWatch(null);

    log(rec.isLoss ? 'warn' : 'success', rec.isLoss ? 'LOSE' : 'WIN', {
      payout: payout?.toString?.(),
      multiplier: rec.actualMult ?? rec.multTarget,
      txHash: meta.txHash,
    });

    setTimeout(() => { setStatus('COOLDOWN'); setTimeout(() => setStatus('IDLE'), COOLDOWN_MS); }, SETTLE_FREEZE_MS);
  }, [
    account, pending, multiplier, pushHistory, sfx, addXp, updateDaily, updateWeeklyMilestone,
    tokenDecimals, log, clearPending,
  ]);

  // Locate latest reveal (log scan)
  const fetchLatestReveal = useCallback(async (receiptBlockHint) => {
    if (!provider || !game || !account) return null;
    const addr  = game.target || game.address;
    const iface = game.interface;
    let topic;
    try { topic = iface.getEventTopic('GameRevealed'); } catch {
      try { topic = iface.getEvent('GameRevealed').topicHash; } catch {}
    }
    const current = await provider.getBlockNumber();
    const span = 6000;
    const from = Math.max(0, (receiptBlockHint || current) - span);
    const to   = current;

    let logs = [];
    try { logs = await provider.getLogs({ address: addr, fromBlock: from, toBlock: to, topics: [topic] }); }
    catch {
      const mid = Math.floor((from + to) / 2);
      try { logs = logs.concat(await provider.getLogs({ address: addr, fromBlock: from, toBlock: mid, topics: [topic] })); } catch {}
      try { logs = logs.concat(await provider.getLogs({ address: addr, fromBlock: mid + 1, toBlock: to, topics: [topic] })); } catch {}
    }

    let latest = null;
    for (const l of logs) {
      try {
        const ev = iface.parseLog(l);
        if (ev?.name !== 'GameRevealed') continue;
        const { player, payout, isLoss, gameType: gt } = ev.args || {};
        if (String(player).toLowerCase() !== account.toLowerCase()) continue;
        if (Number(gt) !== Number(gameType)) continue;
        if (!latest || l.blockNumber > latest.blockNumber) {
          latest = { blockNumber: l.blockNumber, payout, isLoss, txHash: l.transactionHash };
        }
      } catch {}
    }
    return latest;
  }, [provider, game, account, gameType]);

  // Reveal
  const reveal = useCallback((manual = false) =>
    runLocked('Reveal', async () => {
      if (!game || !pending) return;
      if (wrongNetwork) { toast.error(`Wrong network. Switch to ${REQUIRED_CHAIN_NAME}.`); await requestNetworkSwitch(); return; }
      if (!inRevealWindow) {
        if (isExpired) toast.error('Reveal window expired');
        else           toast.info('Reveal not ready yet');
        log('info', 'Reveal blocked (window)', { revealReady, isExpired, earliestBlock, latestBlock, blockNumber });
        return;
      }
      setStatus('REVEALING');

      if (manual) revealTriggeredAtRef.current = Date.now();
      log('info', 'Reveal requested', { manual, gameType });

      try {
        const tx = await game.revealPlay(pending.commitment, pending.param1Raw, pending.param2Raw, pending.secret);
        setPendingTx({ hash: tx.hash, type: 'reveal', startedAt: Date.now() });
        log('info', 'Reveal tx sent', { hash: tx.hash });
        const receipt = await tx.wait();
        setPendingTx(null);

        const iface = game.interface;
        let parsed = null;
        for (const logE of receipt.logs || []) {
          try {
            const pl = iface.parseLog(logE);
            if (pl?.name === 'GameRevealed') {
              const { player, payout, isLoss, gameType: gt } = pl.args || {};
              if (String(player).toLowerCase() === account.toLowerCase() && Number(gt) === Number(gameType)) {
                parsed = { payout, isLoss, txHash: receipt.transactionHash };
                break;
              }
            }
          } catch {}
        }
        if (parsed) {
          log('success', 'Reveal parsed from receipt', { hash: receipt.transactionHash });
          applyResult(parsed.payout, parsed.isLoss, { txHash: parsed.txHash, manual });
          return;
        }

        try {
          const from = Math.max(0, (receipt.blockNumber || 0) - 6);
          const to   = (receipt.blockNumber || 0) + 6;
          const filter = game.filters?.GameRevealed ? game.filters.GameRevealed() : null;
          if (filter) {
            const logs = await game.queryFilter(filter, from, to);
            const hit  = logs.find((l) => {
              const a = l.args || {};
              return String(a.player || '').toLowerCase() === account.toLowerCase()
                && Number(a.gameType) === Number(gameType);
            });
            if (hit) {
              log('success', 'Reveal found via queryFilter', { hash: hit.transactionHash });
              applyResult(hit.args.payout, hit.args.isLoss, { txHash: hit.transactionHash, manual });
              return;
            }
          }
        } catch {}

        setRevealWatch({ txHash: receipt.transactionHash, blockHint: receipt.blockNumber, startedAt: Date.now(), manual });
        toast.info('Reveal confirmed. Waiting for logs…');
        setStatus('SETTLED');
        log('info', 'Reveal confirmed; waiting for log indexing', { hash: receipt.transactionHash });
      } catch (err) {
        setPendingTx(null);
        setStatus('WAITING');
        const msg = err?.shortMessage || err?.message || 'Reveal failed';
        toast.error(msg);
        log('error', 'Reveal failed', { message: err?.message || String(err) });
      }
    }),
  [runLocked, game, pending, wrongNetwork, requestNetworkSwitch, inRevealWindow, isExpired, revealReady, earliestBlock, latestBlock, blockNumber, account, applyResult, log, gameType]);

  // Poll for logs if receipt parsing didn't find them  **(fixed identifier here)**
  useEffect(() => {
    if (!revealWatch || !provider || !game) return;
    let stop = false; // ← fixed
    (async function poll() {
      if (stop) return;
      try {
        const latest = await fetchLatestReveal(revealWatch.blockHint);
        if (latest) {
          log('success', 'Reveal located by log polling', { hash: latest.txHash });
          applyResult(latest.payout, latest.isLoss, { txHash: latest.txHash, manual: revealWatch.manual });
          return;
        }
      } catch {}
      if (Date.now() - revealWatch.startedAt > 30000) {
        log('warn', 'Stopped waiting for logs after 30s');
        return;
      }
      setTimeout(poll, 2000);
    })();
    return () => { stop = true; };
  }, [revealWatch, provider, game, fetchLatestReveal, applyResult, log]);

  // Also handle live GameRevealed events
  useEffect(() => {
    if (!game || !account) return;
    const filter = game.filters?.GameRevealed ? game.filters.GameRevealed() : 'GameRevealed';
    const onRevealed = (...args) => {
      try {
        let player, payout, isLoss, gt, ev;
        if (args[0]?.args) {
          const a = args[0].args;
          player = a.player; payout = a.payout; isLoss = a.isLoss; gt = a.gameType; ev = args[0];
        } else { [player, payout, isLoss, gt, ev] = args; }
        if (String(player).toLowerCase() !== account.toLowerCase()) return;
        if (Number(gt) !== Number(gameType)) return;
        log('success', 'GameRevealed event', { hash: ev?.transactionHash });
        applyResult(payout, isLoss, { txHash: ev?.transactionHash, manual: false });
      } catch {}
    };
    try { game.on(filter, onRevealed); } catch {}
    return () => { try { game.off(filter, onRevealed); } catch {} };
  }, [game, account, applyResult, log, gameType]);

  // Auto‑reveal when ready (bounded)
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      if (stop) return;
      const vis = document.visibilityState === 'visible';
      if (autoReveal && pending && (status === 'WAITING' || status === 'READY')) {
        if (!pending.commitBlock || pending.commitBlock === 0) {
          try {
            const cm = await game.commits(pending.commitment);
            if (cm?.commitBlock) {
              const next = { ...pending, commitBlock: Number(cm.commitBlock), ts: pending.ts || Date.now() };
              setPending(next); savePending(account, next);
            }
          } catch {}
        }
        if (inRevealWindow) {
          log('info', 'Auto reveal triggered');
          await reveal(false);
        } else if (isExpired) {
          log('warn', 'Round expired; stopping auto‑reveal');
        }
      }
      setTimeout(tick, vis ? 1200 : 3000);
    };
    tick();
    return () => { stop = true; };
  }, [autoReveal, pending, status, inRevealWindow, reveal, game, account, log, savePending, isExpired]);

  // Eager reveal exactly on READY (opt‑in)
  useEffect(() => {
    if (!options?.eagerRevealOnReady) return;
    if (!(status === 'READY' && inRevealWindow && !onChainPending)) return;
    const now = Date.now();
    const sameBlock = lastRevealAttemptRef.current.block === blockNumber;
    if (sameBlock || (now - lastRevealAttemptRef.current.t) < AUTO_REVEAL_DEBOUNCE_MS) return;
    lastRevealAttemptRef.current = { t: now, block: blockNumber };
    reveal(true);
  }, [options?.eagerRevealOnReady, status, inRevealWindow, onChainPending, reveal, blockNumber]);

  // If we somehow missed reveal, try to recover from logs
  useEffect(() => {
    (async () => {
      if (onChainPending) return;
      if (pending == null && (status === 'WAITING' || status === 'REVEALING' || status === 'READY')) {
        const latest = await fetchLatestReveal(blockNumber - 20);
        if (latest) {
          log('info', 'Recovered reveal via historical logs', { hash: latest.txHash });
          applyResult(latest.payout, latest.isLoss, { txHash: latest.txHash, manual: false });
        } else {
          setStatus('IDLE');
        }
      }
    })();
  }, [onChainPending, pending, status, fetchLatestReveal, blockNumber, applyResult, log]);

  // Populate global feed
  useEffect(() => {
    if (!game || !provider) return;
    let alive = true;
    (async () => {
      try {
        const iface = game.interface;
        let topic;
        try { topic = iface.getEventTopic('GameRevealed'); } catch {
          try { topic = iface.getEvent('GameRevealed').topicHash; } catch {}
        }
        const current = await provider.getBlockNumber();
        const from = Math.max(0, current - 2500);
        let logs = [];
        try {
          logs = await provider.getLogs({
            address: game.target || game.address,
            fromBlock: from,
            toBlock: current,
            topics: [topic],
          });
        } catch {}
        const parsed = [];
        for (const l of logs) {
          try {
            const ev = iface.parseLog(l);
            if (ev?.name !== 'GameRevealed') continue;
            const { player, payout, isLoss, gameType: gt } = ev.args || {};
            if (Number(gt) !== Number(gameType)) continue;
            parsed.push({ player, payout, isLoss, txHash: l.transactionHash, blockNumber: l.blockNumber });
          } catch {}
        }
        parsed.sort((a, b) => (b.blockNumber || 0) - (a.blockNumber || 0));
        if (alive) setGlobalFeed(parsed.slice(0, GLOBAL_FEED_LIMIT));
      } catch {}
    })();

    const onAny = (...args) => {
      try {
        let player, payout, isLoss, gt, ev;
        if (args[0]?.args) {
          const a = args[0].args;
          player = a.player; payout = a.payout; isLoss = a.isLoss; gt = a.gameType; ev = args[0];
        } else { [player, payout, isLoss, gt, ev] = args; }
        if (Number(gt) !== Number(gameType)) return;
        setGlobalFeed((arr) => [{ player, payout, isLoss, txHash: ev?.transactionHash }, ...arr].slice(0, GLOBAL_FEED_LIMIT));
      } catch {}
    };
    try { game.on('GameRevealed', onAny); } catch {}
    return () => { try { game.off('GameRevealed', onAny); } catch {} alive = false; };
  }, [game, provider, gameType]);

  // Auto mode: keep committing while IDLE
  useEffect(() => {
    if (status === 'IDLE' && autoEnabled) {
      const t = setTimeout(() => {
        if (autoRounds > 0) {
          setAutoRounds((n) => n - 1);
          commit();
        }
      }, 300);
      return () => clearTimeout(t);
    }
  }, [status, autoEnabled, autoRounds, commit]);

  // Hotkeys
  useEffect(() => {
    const onKey = (e) => {
      try {
        const active = typeof document !== 'undefined' ? document.activeElement?.tagName : null;
        if (active && ['INPUT', 'TEXTAREA'].includes(active)) return;
      } catch {}
      const k = e.code || e.key;
      if ((e.ctrlKey || e.metaKey) && String(e.key || '').toLowerCase() === 'k') {
        e.preventDefault(); options?.openPalette?.(); return;
      }
      if (k === 'Space' || k === 'Spacebar') {
        e.preventDefault();
        if (onChainPending && (status === 'READY' || status === 'WAITING') && inRevealWindow) reveal(true);
        else if (!(onChainPending || pending || ['COMMITTING', 'WAITING', 'READY', 'REVEALING'].includes(status))) commit();
      } else if (k === 'ArrowRight') setMultiplierSafe(Number(multiplier) + 0.01);
      else if (k === 'ArrowLeft')  setMultiplierSafe(Number(multiplier) - 0.01);
      else if (k === 'ArrowUp')    setBetInput((v) => String((Number(v || '0') + 1).toFixed(2)));
      else if (k === 'ArrowDown')  setBetInput((v) => String(Math.max(0, Number(v || '0') - 1).toFixed(2)));
      else if (k === 'KeyD') {
        if (!(onChainPending || pending || ['COMMITTING', 'WAITING', 'READY', 'REVEALING'].includes(status))) {
          setBetInput((v) => String((Number(v || '0') * 2).toFixed(2))); commit();
        }
      } else if (k === 'KeyR') {
        if (!(onChainPending || pending || ['COMMITTING', 'WAITING', 'READY', 'REVEALING'].includes(status))) commit();
      }
    };
    const w = safeWindow();
    try { w?.addEventListener?.('keydown', onKey); } catch {}
    return () => { try { w?.removeEventListener?.('keydown', onKey); } catch {} };
  }, [commit, reveal, onChainPending, pending, status, multiplier, setMultiplierSafe, setBetInput, options, inRevealWindow]);

  // Pending tx slow warning
  useEffect(() => {
    if (!pendingTx) return;
    const id = setTimeout(() => {
      toast.info('Transaction is still pending — you can speed it up in your wallet or open it on the explorer.');
      log('warn', 'Transaction pending >45s', { hash: pendingTx.hash, type: pendingTx.type });
    }, 45000);
    return () => clearTimeout(id);
  }, [pendingTx, log]);

  // UI helpers for countdown & rpc health
  const blocksToReady = useMemo(() => {
    if (!pending?.commitBlock || !blockNumber) return null;
    const left = pending.commitBlock + revealDelay - blockNumber;
    return left > 0 ? left : 0;
  }, [pending, blockNumber, revealDelay]);
  const blockProgress = useMemo(() => {
    if (!pending?.commitBlock || !blockNumber) return 0;
    const waited = Math.max(0, blockNumber - pending.commitBlock);
    const linear = clamp(waited / revealDelay, 0, 1);
    return Math.pow(linear, 1.6);
  }, [pending, blockNumber, revealDelay]);

  const blockAgeSec = Math.max(0, Math.floor((Date.now() - lastBlockTs) / 1000));
  const rpcHealth   = blockAgeSec < 8 ? 'Good' : blockAgeSec < 20 ? 'Slow' : 'Degraded';

  const lobbyEnergy = useMemo(() => {
    if (!globalFeed.length) return 0;
    const sample = globalFeed.slice(0, 20);
    const wins = sample.filter((s) => !s.isLoss).length;
    return Math.round(100 * (wins / sample.length));
  }, [globalFeed]);

  const level = 1 + Math.floor((prog.season?.xp || 0) / 120);
  const dailyCount = prog.daily?.count || 0;
  const weeklyNear150 = prog.weekly?.near150Wins || 0;

  // Final API
  return {
    // meta
    gameType, status, paused, config, maxProfit, allowance, chainId,

    // inputs
    betInput, setBetInput,
    multiplier, setMultiplierSafe, setRollOver, sliderBounds,

    tokenDecimals, tokenSymbol, account,

    // estimates
    estPayout, estProfit, profitExceedsCap,

    // reveal window + rpc
    revealReady, earliestBlock, latestBlock, isExpired, inRevealWindow, blocksToReady, blockProgress,
    blockNumber, blockAgeSec, rpcHealth,

    // round state
    lastResult, setLastResult,
    history, confetti,
    muted, setMuted,
    pending, onChainPending,
    autoReveal, setAutoReveal,
    tab, setTab,
    autoRounds, setAutoRounds,
    globalFeed,
    reducedMotion, setReducedMotion,
    highContrast, setHighContrast,
    lobbyEnergy,

    // network
    wrongNetwork,
    requestNetworkSwitch,

    // actions
    commit, reveal,
    expire: useCallback(
      () => runLocked('Expire', async () => {
        const cands = [contracts?.game, contracts?.router, contracts?.main, contracts?.core].filter(Boolean);
        const names = ['timeoutReveal', 'expireCommit', 'expire', 'timeout', 'expireStale'];
        for (const c of cands) {
          for (const n of names) {
            const fn = c?.[n];
            if (typeof fn === 'function') {
              try { await fn(Number(gameType)); toast.success('Expire sent'); log('success', 'Expire called', { name: n }); return true; }
              catch {
                try { await fn(); toast.success('Expire sent'); log('success', 'Expire called (no‑arg)', { name: n }); return true; }
                catch { /* try next */ }
              }
            }
          }
        }
        toast.info('Expire not available via current ABI');
        return false;
      }),
      [runLocked, contracts, gameType, log]
    ),
    approveExact, approveUnlimited,
    setParamPackers,

    // convenience
    rebet: useCallback(() => {
      const roundActive = onChainPending || !!pending || ['COMMITTING', 'WAITING', 'READY', 'REVEALING'].includes(status);
      if (roundActive) { toast.info('Finish reveal first'); }
      else { commit(); log('info', 'Rebet triggered'); }
    }, [onChainPending, pending, status, commit, log]),
    doubleOrNothing: useCallback(() => {
      const roundActive = onChainPending || !!pending || ['COMMITTING', 'WAITING', 'READY', 'REVEALING'].includes(status);
      if (roundActive) { toast.info('Finish reveal first'); log('info', 'Double blocked: round active'); return; }
      setBetInput((v) => String((Number(v || '0') * 2).toFixed(2)));
      commit();
      log('info', 'Double or Nothing triggered');
    }, [onChainPending, pending, status, commit, log]),

    // progress & pnl
    level, dailyCount, weeklyNear150, sessionSecs,
    guardEnabled, setGuardEnabled,
    guardStopLoss, setGuardStopLoss,
    guardTakeProfit, setGuardTakeProfit,
    sessionPnL,

    // finance
    minBetUnits, maxBetUnits, betUnits,

    // tx
    pendingTx,
  };
}

// =======================================
// Back‑compat wrappers for Crash pages
// =======================================
export function useCrashContracts(signer) { return useGameContracts(signer, GAME_TYPE.CRASH); }
export function useCrashFlow(contracts, options) { return useGameFlow(contracts, options, GAME_TYPE.CRASH); }
